import {
  Box,
  CircularProgress,
  FormControl,
  InputLabel,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  MenuItem,
  Select,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useEffect, useState } from "react";
import dayjs, { Dayjs } from "dayjs";
import CreateAppointmentModal from "./CreateAppointmentModal";
import ComercialDrawer from "../../../../components/pet-beauty/CormercialDrawer";
import { AppointmentDetailsModal } from "../../../../components/general/AppointmentDetailsModal";
import GenericModal from "../../../../components/general/GenericModal";
import AppointmentsCalendar from "../../../../components/general/AppointmentsCalendar";
import useFetchComercialAppointments from "../../../../hooks/entities/useFetchComercialAppointments";
import { generalDarkGrey } from "../../../../styles/colors";
import { sharedStyles } from "../../../../styles/shared";
import { formatDateAndTimeDayDateToString } from "../../Client/ScheduilingPage/timeUtils";
import AppointmentStatusChip from "../../../../components/general/AppointmentStatusChip";
import {
  AppointmentDates,
  certainDateHasAppointments,
  getPendentConfirmedCanceledAndFinishedAppointmentDatesFromUserAppointments,
  isAppointmentFromCertainDate,
} from "../../../../utils/dateFunctions";
import BackPageButton, { LeftMarginBackButton } from "../../../../components/general/buttons/BackPageButton";
import { IAppointment } from "../../../../services/api/Appointment/types";
import { handleFinishAppointmentClick } from "../../../../components/general/AppointmentDetailsModal/functions";
import { useTranslation } from "react-i18next";
import orderApi from "@/services/api/Order";

const ComercialAppointments = () => {
  const { t } = useTranslation();
  const isMobile = useMediaQuery("(max-width:1000px)");

  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(dayjs());

  const {
    appointments,
    isLoading: appointmentsLoading,
    employeeStores,
    userEmployees,
    refetch: fetchAllUserRelatedAppointments,
  } = useFetchComercialAppointments();

  const [selectedStoreId, setSelectedStoreId] = useState<string>("-");

  const [selectedAppointment, setSelectedAppointment] =
    useState<IAppointment | null>(null);

  const [createAppointmentModalOpen, setCreateAppointmentModalOpen] =
    useState(false);

  const [finishAppointmentLoading, setFinishAppointmentLoading] =
    useState(false);

  const handleOpenAppointmentDetailsModal = (
    selectedAppointment: IAppointment
  ) => setSelectedAppointment(selectedAppointment);

  const handleFinishAppointment = async () => {
    setFinishAppointmentLoading(true);
    await handleFinishAppointmentClick(selectedAppointment?.id);
    await fetchAllUserRelatedAppointments();

    setSelectedAppointment(null);
    setFinishAppointmentLoading(false);
  };

  useEffect(() => {
    orderApi.syncUserOrders().catch(() => {});
  }, []);

  useEffect(() => {
    employeeStores && employeeStores.length > 0 && setSelectedStoreId("-");
  }, [employeeStores]);

  const filteredAppointments =
    selectedStoreId === "-" || !selectedStoreId
      ? appointments
      : appointments.filter(
          (appointment) =>
            appointment.serviceEmployee.Employee.storeId === selectedStoreId
        );

  const appointmentDates: AppointmentDates = appointments
    ? getPendentConfirmedCanceledAndFinishedAppointmentDatesFromUserAppointments(
        filteredAppointments
      )
    : { pendent: [], canceled: [], confirmed: [], finished: [] };

  const renderAppointmentsCalendar = (
    <AppointmentsCalendar
      pendentAppointmentDates={appointmentDates?.pendent}
      confirmedAppointmentDates={appointmentDates?.confirmed}
      canceledAppointmentDates={appointmentDates?.canceled}
      finishedAppointementDates={appointmentDates?.finished}
      selectedDate={selectedDate}
      setSelectedDate={setSelectedDate}
    />
  );

  const renderAppointmentsList = (
    <Box
      width={isMobile ? "100%" : "38%"}
      sx={{ ...sharedStyles.verticalContent }}
    >
      {/* <PrimaryButton
        label={` Criar agendamento (${selectedDate?.format("DD/MM/YYYY")})`}
        icon={<AddAlertIcon />}
        onClickAction={() => setCreateAppointmentModalOpen(true)}
      /> */}

      <Box
        sx={{
          ...sharedStyles.horizontalContent,
          justifyContent: "space-between",
        }}
      >
        <Typography variant="h6">
          {selectedDate?.format("DD/MM/YYYY")}
        </Typography>

        <FormControl sx={{ width: "170px" }}>
          <InputLabel shrink>{t("appointmentDetails.comercialAppointments.storeLabel")}</InputLabel>
          <Select
            notched
            label={t("appointmentDetails.comercialAppointments.storeLabel")}
            value={selectedStoreId}
            onChange={(e) => setSelectedStoreId(e.target.value)}
            size="small"
          >
            {employeeStores?.map((store) => (
              <MenuItem key={store?.id} value={store.id}>
                <ListItem disablePadding>
                  <ListItemText primary={store?.name} />
                </ListItem>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {certainDateHasAppointments(filteredAppointments, selectedDate) ? (
        <List
          sx={{
            ...sharedStyles.appointmentsList,
            height: "380px",
            overflowY: "auto",
          }}
          disablePadding
        >
          {filteredAppointments.map(
            (appointment, index) =>
              isAppointmentFromCertainDate(appointment, selectedDate) && (
                <ListItemButton
                  onClick={() => handleOpenAppointmentDetailsModal(appointment)}
                  key={appointment.id}
                  sx={sharedStyles.horizontalContent}
                >
                  <ListItemText
                    primary={`${filteredAppointments[index].serviceEmployee.ServiceStore.serviceName} `}
                    secondary={`${formatDateAndTimeDayDateToString(
                     filteredAppointments[index].date, t
                    )}${t("appointmentDetails.comercialAppointments.with")}${
                      filteredAppointments[index].serviceEmployee.Employee.User
                        .name
                    } (${
                      filteredAppointments[index].serviceEmployee.Employee.store
                        .name
                    }) `}
                    primaryTypographyProps={{ fontWeight: 500 }}
                  />
                  <AppointmentStatusChip
                    status={filteredAppointments[index].status}
                  />
                </ListItemButton>
              )
          )}
        </List>
      ) : (
        <Box>
          <Typography color={generalDarkGrey}>
            {t("appointmentDetails.comercialAppointments.noAppointments")}
          </Typography>
        </Box>
      )}
    </Box>
  );

  const renderAppointmentsSection = (
    <Box sx={sharedStyles.verticalContent}>
      {!appointmentsLoading ? (
        <>
          <LeftMarginBackButton />
          
          <Box
            sx={{
              ...sharedStyles.dividedFormContainer,
              flexDirection: isMobile ? "column" : "row",
            }}
          >
            {renderAppointmentsCalendar}
            {renderAppointmentsList}
          </Box>
        </>
      ) : (
        <Box
          sx={{
            ...sharedStyles.verticalContent,
            alignItems: "center",
            marginTop: "50px",
          }}
        >
          <CircularProgress />
        </Box>
      )}
    </Box>
  );

  return (
    <Box sx={sharedStyles.sharedMainContainer}>
      <GenericModal
        onClose={() => setCreateAppointmentModalOpen(false)}
        open={createAppointmentModalOpen}
        content={
          <CreateAppointmentModal
            onClose={() => setCreateAppointmentModalOpen(false)}
            selectedDate={selectedDate}
            employeeStores={employeeStores}
            userEmployees={userEmployees}
          />
        }
      />
      <AppointmentDetailsModal
        appointment={selectedAppointment}
        onClose={() => setSelectedAppointment(null)}
        onFinish={handleFinishAppointment}
        showClient
        loading={finishAppointmentLoading}
      />
      <ComercialDrawer content={renderAppointmentsSection} />
    </Box>
  );
};

export default ComercialAppointments;
